import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Loader2, FlaskConical, Search, AlertCircle } from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';
import { useAuthStore } from '@/stores/auth';
import {
  isSupportDeskRole, platformBadge, STATUS_STYLES, statusLabel,
  BUG_FIX_STATUSES, type BugTestListItem,
} from './bugTestConstants';

// Bug Test Dashboard (list) — autonomous bug-fix runs awaiting human review.
// Desk-roles-only (mirrors SupportTicketsPage L229-WEB-3 gate); the backend
// independently enforces the desk gate on GET /support/bug-fix-runs.

export function BugTestDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [runs, setRuns] = useState<BugTestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [search, setSearch] = useState('');

  const fetchRuns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (platformFilter) params.set('platform', platformFilter);
      if (search.trim()) params.set('search', search.trim());
      const qs = params.toString();
      const res = await api.get(`/support/bug-fix-runs${qs ? `?${qs}` : ''}`);
      const data = res.data.data?.runs ?? [];
      setRuns(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to load bug-fix runs';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, platformFilter, search]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Desk gate — while the auth store hydrates render nothing (no flash, no
  // fetch side-effects on top of the redirect).
  if (!user) return null;
  if (!isSupportDeskRole(user.role?.name)) {
    return <Navigate to="/support/my" replace />;
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Bug Test Dashboard</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Autonomous bug-fix runs awaiting review.</p>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search ticket number or subject"
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">All statuses</option>
          {BUG_FIX_STATUSES.map(s => (
            <option key={s} value={s}>{statusLabel(s)}</option>
          ))}
        </select>
        <select
          value={platformFilter}
          onChange={e => setPlatformFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">All platforms</option>
          <option value="web">Web</option>
          <option value="mobile">Mobile</option>
          <option value="backend">Backend</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={22} className="animate-spin text-[var(--saffron-600)]" />
        </div>
      ) : error ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle size={18} /> {error}
        </div>
      ) : runs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 py-16 text-gray-500 dark:border-gray-700 dark:bg-gray-900">
          <FlaskConical size={32} className="mb-2 text-gray-300" />
          <div className="text-sm">No bug-fix runs</div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                <th className="px-4 py-2.5">Ticket</th>
                <th className="px-4 py-2.5">Subject</th>
                <th className="px-4 py-2.5">Platform</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Confidence</th>
                <th className="px-4 py-2.5">Updated</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(run => {
                const badge = platformBadge(run.platform);
                return (
                  <tr
                    key={run.id}
                    onClick={() => navigate(`/support/bug-tests/${run.id}`)}
                    className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800"
                  >
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{run.supportTicket?.number || '—'}</td>
                    <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                      {run.supportTicket?.subject || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
                        <span>{badge.emoji}</span> {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[run.status] || STATUS_STYLES.queued}`}>
                        {statusLabel(run.status)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-400">
                      {run.confidence ? run.confidence.replace(/_/g, ' ') : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(run.updatedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
